import { canonicalSceneJson } from "@excalidraw-ms/shared";
import * as backend from "../api/backendClient";

export type ApplicationRequest =
  | { tool: "drawing_open"; operationId: string; drawingId: string }
  | { tool: "drawing_save"; operationId: string; name?: string }
  | {
      tool: "drawing_share";
      operationId: string;
      action: "create" | "start_session";
      role: "viewer" | "editor";
    };

export interface ApplicationResult {
  status: string;
  message?: string;
  drawingId?: string;
  url?: string;
  role?: string;
  backendRequestId?: string;
}

export class ApplicationToolError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export function applicationError(error: unknown): ApplicationResult & { code: string } {
  if (error instanceof ApplicationToolError) {
    return { status: "error", code: error.code, message: error.message };
  }
  return {
    status: "error",
    ...(error instanceof backend.BackendError && error.requestId
      ? { backendRequestId: error.requestId }
      : {}),
    code:
      error instanceof backend.BackendError && error.status === 403
        ? "ACCESS_DENIED"
        : "APPLICATION_UNAVAILABLE",
    message:
      "The operation could not be completed. Check the app and retry after resolving the error.",
  };
}

export function objectInput(input: unknown, keys: readonly string[]): Record<string, unknown> {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw new ApplicationToolError("INVALID_INPUT", "Expected an object.");
  }
  const result = input as Record<string, unknown>;
  if (Object.keys(result).some((key) => !keys.includes(key))) {
    throw new ApplicationToolError("INVALID_INPUT", "Unknown request field.");
  }
  return result;
}

function text(value: unknown, max: number, field: string): string {
  if (typeof value !== "string" || !value.trim() || value.length > max) {
    throw new ApplicationToolError("INVALID_INPUT", `Invalid ${field}.`);
  }
  return value;
}

export function parseApplicationRequest(
  tool: ApplicationRequest["tool"],
  input: unknown,
): ApplicationRequest {
  const fields =
    tool === "drawing_open"
      ? ["operationId", "drawingId"]
      : tool === "drawing_save"
        ? ["operationId", "name"]
        : ["operationId", "action", "role"];
  const value = objectInput(input, fields);
  const operationId = text(value.operationId, 100, "operationId");
  if (tool === "drawing_open") {
    const drawingId = text(value.drawingId, 36, "drawingId");
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(drawingId)) {
      throw new ApplicationToolError("INVALID_INPUT", "Use a drawing ID returned by drawing_list.");
    }
    return { tool, operationId, drawingId };
  }
  if (tool === "drawing_save") {
    return {
      tool,
      operationId,
      ...(value.name === undefined ? {} : { name: text(value.name, 120, "name").trim() }),
    };
  }
  if (
    (value.action !== "create" && value.action !== "start_session") ||
    !["viewer", "editor"].includes(String(value.role))
  ) {
    throw new ApplicationToolError(
      "INVALID_INPUT",
      "Specify action create or start_session and a viewer or editor role.",
    );
  }
  return { tool, operationId, action: value.action, role: value.role as "viewer" | "editor" };
}

interface Receipt {
  request: string;
  result: ApplicationResult;
  expires: number;
}

/** Account-scoped, page-memory receipts survive editor remounts, not reloads. */
export class ApplicationReceipts {
  private account = "";
  private scope = 0;
  private entries = new Map<string, Receipt>();
  setAccount(account: string): void {
    if (account !== this.account) {
      this.entries.clear();
      this.scope++;
    }
    this.account = account;
  }
  isAccount(account: string): boolean {
    return this.account === account;
  }
  getScope(): number {
    return this.scope;
  }
  private prune(): void {
    for (const [id, receipt] of this.entries) {
      if (receipt.expires <= Date.now()) this.entries.delete(id);
    }
  }
  get(request: ApplicationRequest): ApplicationResult | undefined {
    this.prune();
    const entry = this.entries.get(request.operationId);
    if (entry && entry.request !== canonicalSceneJson(request)) {
      throw new ApplicationToolError(
        "OPERATION_ID_REUSED",
        "Use a new operationId for a different request.",
      );
    }
    return entry ? structuredClone(entry.result) : undefined;
  }
  start(request: ApplicationRequest): void {
    this.prune();
    if (this.entries.size >= 32) {
      throw new ApplicationToolError(
        "RATE_LIMITED",
        "Too many recent application operations. Wait before submitting more.",
      );
    }
    this.entries.set(request.operationId, {
      request: canonicalSceneJson(request),
      result: {
        status: "confirmation_required",
        message:
          "Approve this action in the Excalidraw dialog, then repeat this identical request for its result.",
      },
      expires: Date.now() + 10 * 60_000,
    });
  }
  finish(request: ApplicationRequest, result: ApplicationResult): void {
    const entry = this.entries.get(request.operationId);
    if (entry?.request === canonicalSceneJson(request)) entry.result = structuredClone(result);
  }
}

export async function listApplicationDrawings(input: unknown, getToken: () => Promise<string>) {
  const value = objectInput(input, ["scope", "cursor", "query", "limit"]);
  const scope = value.scope ?? "mine";
  if (scope !== "mine" && scope !== "shared")
    throw new ApplicationToolError("INVALID_INPUT", "scope must be mine or shared.");
  const limit = value.limit ?? 20;
  if (typeof limit !== "number" || !Number.isInteger(limit) || limit < 1 || limit > 20) {
    throw new ApplicationToolError("INVALID_INPUT", "limit must be an integer from 1 to 20.");
  }
  const cursor = value.cursor === undefined ? undefined : text(value.cursor, 8192, "cursor");
  const query =
    value.query === undefined ? undefined : text(value.query, 120, "query").toLowerCase();
  const token = await getToken();
  if (!token) throw new ApplicationToolError("ACCESS_DENIED", "Sign in to use cloud drawings.");
  const page =
    scope === "mine"
      ? await backend.listOwnedDrawings(token, limit, cursor)
      : await backend.listSharedDrawings(token, limit, cursor);
  const details = page.drawings.length
    ? await backend.getDrawingGalleryDetails(
        token,
        page.drawings.map((item) => item.drawingId),
      )
    : { drawings: [] };
  const names = new Map(details.drawings.map((item) => [item.drawingId, item.name]));
  const entries = page.drawings.map((item) => ({
    drawingId: item.drawingId,
    name: names.get(item.drawingId)?.slice(0, 120) ?? null,
    role: item.role,
    updatedAt: item.updatedAt,
  }));
  return {
    status: "ok",
    drawings: query ? entries.filter((item) => item.name?.toLowerCase().includes(query)) : entries,
    scannedCount: entries.length,
    unavailableTitleCount: entries.filter((item) => item.name === null).length,
    queryScope: "current_page",
    complete: !page.continuationToken,
    nextCursor: page.continuationToken,
    totalCount: page.totalCount,
    message:
      "Follow nextCursor to search remaining pages. Missing titles cannot be matched. No scene contents are returned.",
  };
}
